import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import merchantApiClient from '@/services/merchantApiClient';
import { useEnvironmentStore } from '@/store/useEnvironmentStore';
import { isPlatformAllowed } from '@/utils/platform';

function sanitizeProfile(profile) {
  if (!profile) return null;
  const sanitized = { ...profile };
  // Strip the raw bcrypt password hash (backend bug — leaked in the response, no
  // legitimate client-side use). `sky` (secretKey) is kept: it's the merchant's own
  // real Collection API credential, same as merchant-frontend's dashboard shows it.
  delete sanitized.password;
  return sanitized;
}

// Mirrors merchant-frontend's dashboard "API Credentials" widget (Client ID = userName,
// Secret Key = sky) by filling the locked environment's merchantID/secretKey variables
// from the merchant's own profile, so Collection API hash-auth works without manual entry.
function syncCollectionApiCredentials(profile) {
  if (!profile) return;
  const envStore = useEnvironmentStore.getState();
  const env = envStore.activeEnv();
  if (!env) return;

  const variables = env.variables.map((v) => {
    if (v.key === 'merchantID') return { ...v, value: profile.userName || v.value };
    if (v.key === 'secretKey') return { ...v, value: profile.sky || v.value };
    return v;
  });
  envStore.updateEnvironment(env.id, { variables });

  // eslint-disable-next-line no-console
  console.log('[auth] synced Collection API credentials from profile', {
    profileFields: Object.keys(profile),
    userName: profile.userName,
    sky: profile.sky,
    resultingVariables: variables,
  });
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      merchant: null,
      status: 'idle',
      error: null,

      login: async ({ email, password }) => {
        if (!isPlatformAllowed()) {
          const error = 'The Developer Portal is only supported on Windows.';
          set({ status: 'error', error });
          return { success: false, error };
        }

        set({ status: 'loading', error: null });
        try {
          const res = await merchantApiClient.post('/api/auth/login', { email, password });
          const data = res.data?.data;

          if (data?.twoFaRequired) {
            const error = '2FA is not yet supported in the developer portal — please sign in via the merchant dashboard.';
            set({ status: 'error', error });
            return { success: false, error };
          }

          set({ accessToken: data.accessToken, refreshToken: data.refreshToken, status: 'idle', error: null });
          await get().fetchProfile();
          return { success: true };
        } catch (err) {
          const error = err.response?.data?.message || err.message || 'Login failed';
          set({ status: 'error', error });
          return { success: false, error };
        }
      },

      fetchProfile: async () => {
        const res = await merchantApiClient.get('/api/auth/profile');
        // eslint-disable-next-line no-console
        console.log('[auth] GET /api/auth/profile raw response', res.data);
        const profile = sanitizeProfile(res.data?.data);
        set({ merchant: profile });
        syncCollectionApiCredentials(profile);
      },

      refreshTokens: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error('No refresh token available');
        const res = await merchantApiClient.post('/api/auth/refresh-token', { token: refreshToken });
        const data = res.data?.data;
        set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
        return data.accessToken;
      },

      logout: () => set({ accessToken: null, refreshToken: null, merchant: null, status: 'idle', error: null }),
    }),
    {
      name: 'albecollect_auth',
      partialize: (s) => ({ accessToken: s.accessToken, refreshToken: s.refreshToken, merchant: s.merchant }),
    }
  )
);

export const isAuthenticated = (state) => !!state.accessToken;
